/* eslint-disable max-lines */
import { useLastEscalation } from '@hakimo-ui/hakimo/data-access';
import {
  Situation,
  SituationColor,
  SOPException,
  SOPNote,
} from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';
import { LastEscalationInfo } from '../last-escalation-info/LastEscalationInfo';
import { LocationNotes } from '../location-notes/LocationNotes';
import CameraLabels from '../sop-manager/camera-labels/CameraLabels';
import { ScheduleLabels } from '../sop-manager/schedule-labels/ScheduleLabels';
import TimeRangeLabels from '../sop-manager/time-range-labels/TimeRangeLabels';
import { DetachableSection } from './DetachableSection';
import { getRelevantSchedules } from './scheduleUtils';

interface Props {
  exceptions?: SOPException[];
  notes?: SOPNote[];
  situations?: Situation[];
  onAction?: (selectedSituation: Situation) => void;
  locationId: string;
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
  locationTimezone?: string;
}

export function SOPFirstStep(props: Props) {
  const {
    exceptions,
    notes,
    situations,
    onAction,
    locationId,
    alarmStartEpochMs,
    alarmEndEpochMs,
    locationTimezone,
  } = props;

  const onSituationSelect = (situation: Situation) => {
    onAction?.(situation);
  };

  const { data: recentEscalationData } = useLastEscalation(locationId);
  const escalation = recentEscalationData?.escalation;
  const escalationDetailsHref =
    escalation?.id && escalation?.source_system
      ? escalation.source_system === 'Vision'
        ? `/alarm-groups/${escalation.id}`
        : `/location-alarms/${escalation.id}`
      : undefined;

  return (
    <div className="text-onlight-text-2 dark:text-ondark-text-2 flex flex-col space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <div className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center gap-2 font-sans font-semibold">
            <span>Last Escalated</span>
            {escalationDetailsHref && (
              <a
                href={escalationDetailsHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
          <LastEscalationInfo locationId={locationId} />
        </div>
        <LocationNotes locationId={locationId} />
      </div>

      {/* Situations Section (Scrollable) */}
      {situations && situations.length > 0 && (
        <div className="py-3">
          <div className="text-onlight-text-1 dark:text-ondark-text-1 mb-1 font-sans font-semibold">
            Situations (Select if observed)
          </div>
          <div className="flex flex-wrap gap-2 py-1 pr-2">
            {situations.map((situation, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSituationSelect?.(situation)}
                className={clsx(
                  'border-onlight-line-2 dark:border-ondark-line-2 hover:dark:bg-ondark-bg-2 hover:bg-onlight-bg-2 flex flex-wrap items-center gap-2 rounded-md border p-2',
                  situation.color === SituationColor.RED &&
                    'text-situation-rose-600 hover:text-situation-rose-700 dark:text-situation-rose-400',
                  situation.color === SituationColor.GREEN &&
                    'text-situation-teal-600 hover:text-situation-teal-700 dark:text-situation-teal-400',
                  situation.color === SituationColor.BLUE &&
                    'text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
                )}
              >
                <span className="text-left text-sm font-medium">
                  {situation.label}
                </span>
                <ScheduleLabels
                  schedules={getRelevantSchedules(
                    situation,
                    alarmStartEpochMs,
                    alarmEndEpochMs,
                    locationTimezone ?? 'UTC'
                  )}
                />
                {situation.cameras && situation.cameras.length > 0 && (
                  <CameraLabels cameras={situation.cameras} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {exceptions && exceptions.length > 0 && (
        <DetachableSection
          sectionId="exceptions"
          title="Exceptions (Do not escalate if observed)"
        >
          <ul className="list-disc space-y-2 overflow-hidden">
            {exceptions.map((exception, i) => (
              <li key={i} className="ml-4 list-item">
                <div className="flex flex-wrap items-center gap-2 [overflow-wrap:anywhere]">
                  <span>{exception.text}</span>
                  <TimeRangeLabels timeRange={exception.timeRange} />
                  <CameraLabels cameras={exception.cameras} />
                </div>
              </li>
            ))}
          </ul>
        </DetachableSection>
      )}

      {/* Notes Section */}
      {notes && notes.length > 0 && (
        <DetachableSection sectionId="notes" title="Notes">
          <ul className="min-w-0 list-disc space-y-2 overflow-hidden">
            {notes.map((note, i) => (
              <li key={i} className="ml-4 list-item">
                <div className="flex flex-wrap items-center gap-2 [overflow-wrap:anywhere]">
                  <span>{note.text}</span>
                  <TimeRangeLabels timeRange={note.timeRange} />
                  <CameraLabels cameras={note.cameras} />
                </div>
              </li>
            ))}
          </ul>
        </DetachableSection>
      )}
    </div>
  );
}
